import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Sun, Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Overline } from "../components/Overline";
import { SERVICES, TESTIMONIALS, MEDIA, SITE } from "../lib/site";
import { Seo } from "../components/Seo";
import { AimaMeaning } from "../components/AimaMeaning";

export default function Home() {
  return (
    <div data-testid="page-home">
      <Seo title="" description="Holistic hormone health for women who refuse to be dismissed. PCOS, perimenopause, thyroid, PMS and fertility awareness with Lara Brown." path="/" />
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden sheen-sage"
      >
        <div
          className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full bg-[#cbdad6]/40 blur-3xl pointer-events-none float-slow"
          aria-hidden
        />
        <div
          className="absolute -bottom-40 -right-40 w-[700px] h-[700px] rounded-full bg-[#f3eee8]/60 blur-3xl pointer-events-none float-slower"
          aria-hidden
        />
        <svg
          className="deco-leaf top-16 right-10 w-32 h-32 hidden md:block float-slow"
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          aria-hidden
        >
          <path d="M50 5 C20 30, 20 70, 50 95 C80 70, 80 30, 50 5 Z" />
          <path d="M50 5 L50 95" />
        </svg>
        <svg
          className="deco-leaf bottom-20 left-8 w-24 h-24 hidden md:block float-slower"
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          aria-hidden
        >
          <circle cx="50" cy="50" r="40" />
          <circle cx="50" cy="50" r="28" />
          <circle cx="50" cy="50" r="16" />
        </svg>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28 lg:py-36">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 fade-up">
              <Overline data-testid="hero-overline">
                Personalised · Root-Cause · Proactive
              </Overline>
              <h1
                data-testid="hero-headline"
                className="font-serif mt-6 text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1.02] font-normal text-[#333]"
              >
                Hormone health
                <br />
                for women who
                <br />
                <span className="italic text-[#5fa292]">refuse to be dismissed.</span>
              </h1>
              <p
                data-testid="hero-subhead"
                className="mt-8 text-lg md:text-xl text-[#3d3d3d] max-w-xl font-normal leading-relaxed"
              >
                I'm Lara, a women's holistic hormone health practitioner who
                listens. If you've been told it's all in your head, or that
                your labs are 'normal' - you're in the right place.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/contact" data-testid="hero-cta-primary">
                  <Button className="bg-[#5fa292] hover:bg-[#4d8275] text-white rounded-full px-8 py-6 h-auto text-base font-medium tracking-wide">
                    Book a discovery call
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/work-with-me" data-testid="hero-cta-secondary">
                  <Button className="bg-white hover:bg-[#f3eee8] text-[#333] border border-[#5fa292]/40 rounded-full px-8 py-6 h-auto text-base font-medium tracking-wide shadow-sm">
                    Ways to work together
                  </Button>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 fade-up delay-2">
              <div className="relative">
                <div
                  className="absolute -top-6 -left-6 w-32 h-32 bg-[#cbdad6] rounded-full blur-2xl opacity-60"
                  aria-hidden
                />
                <div className="arch-image relative aspect-[4/5] bg-[#cbdad6]">
                  <img
                    src={MEDIA.hero}
                    alt="Woman in soft natural light"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-4 bg-[#faf9f6] border border-[#e5e0d8] rounded-2xl p-5 shadow-xl shadow-[#5fa292]/5 max-w-[200px]">
                  <p className="font-serif text-2xl text-[#5fa292] leading-none">
                    8+
                  </p>
                  <p className="text-xs text-[#3d3d3d] mt-2 leading-snug">
                    years walking women home to their bodies
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY STRIP */}
      <section className="py-20 md:py-28 bg-[#f3eee8]/60 border-y border-[#e5e0d8]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Overline>The Aima approach</Overline>
          <p className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#333] mt-6 leading-[1.15] font-normal tracking-tight">
            "Your symptoms aren't an inconvenience to be silenced.
            <br className="hidden md:block" />
            They are a language your body is asking you to learn."
          </p>
          <p className="mt-8 text-sm uppercase tracking-[0.2em] text-[#5fa292]">
            - Lara Brown
          </p>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section
        data-testid="services-grid"
        className="py-20 md:py-32 max-w-7xl mx-auto px-6 md:px-12 lg:px-16"
      >
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-16">
          <div className="lg:col-span-5">
            <Overline>What I work with</Overline>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight font-normal text-[#333] mt-4">
              The conditions
              <br />
              women are told to
              <br />
              <span className="italic">just live with.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-lg text-[#3d3d3d] font-normal leading-relaxed">
              You know when something isn't right and I work with you as a
              personal health concierge to get to the root, not just deal with
              the symptoms. We use functional testing, mitochondrial support
              and effective nutrition that fits your real life and goals.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {SERVICES.slice(0, 4).map((s, i) => (
            <div
              key={s.slug}
              data-testid={`service-card-${s.slug}`}
              className={`group relative p-8 md:p-10 rounded-3xl border border-[#e5e0d8] transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#5fa292]/5 ${
                i % 3 === 0 ? "bg-[#cbdad6]/40" : "bg-white"
              }`}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#5fa292] text-white flex items-center justify-center text-sm font-medium">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-[#333] font-normal tracking-tight">
                  {s.title}
                </h3>
              </div>
              <p className="text-[#3d3d3d] font-normal leading-relaxed">
                {s.summary}
              </p>
              <ul className="mt-5 space-y-1.5">
                {s.points.map((p) => (
                  <li
                    key={p}
                    className="text-sm text-[#3d3d3d] flex items-center gap-2 font-normal"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#5fa292]" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Link to="/services" data-testid="services-view-all">
            <Button
              variant="outline"
              className="border-[#5fa292] text-[#5fa292] hover:bg-[#cbdad6]/40 rounded-full px-8 h-12 bg-transparent"
            >
              See full services <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section
        data-testid="about-lara"
        className="py-20 md:py-32 bg-[#f3eee8]/40"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5">
            <div className="arch-image aspect-[4/5] bg-[#cbdad6]">
              <img
                src={MEDIA.portrait}
                alt="Lara Brown, holistic hormone health practitioner"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <Overline>Meet Lara</Overline>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight font-normal text-[#333] mt-4">
              I built Aima Health for
              <br />
              <span className="italic">the woman I used to be.</span>
            </h2>
            <p className="mt-8 text-lg text-[#3d3d3d] font-normal leading-relaxed">
              Years of being handed the pill, dismissed by GPs, and told my
              blood tests were "fine" while I was anything but, led me into functional
              medicine and circadian health. What I learned changed my life.
              Now I help other women know their bodies and empower them in
              creating true health.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6">
              {[
                { icon: Sparkles, label: "MBLI-certified" },
                { icon: Sun, label: "Circadian-trained" },
                { icon: Heart, label: "Women-only practice" },
              ].map((c, i) => (
                <div key={i} className="text-center">
                  <c.icon className="mx-auto text-[#5fa292] mb-2" size={22} />
                  <p className="text-xs text-[#3d3d3d] tracking-wide">
                    {c.label}
                  </p>
                </div>
              ))}
            </div>
            <Link to="/about" className="inline-block mt-10" data-testid="home-about-link">
              <Button className="bg-[#5fa292] hover:bg-[#4d8275] text-white rounded-full px-8 h-12">
                More about Lara
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL PEEK */}
      <section className="py-20 md:py-32 max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center">
          <Overline>Words from women</Overline>
          <div className="mt-8 relative">
            <span className="font-serif absolute -top-8 left-1/2 -translate-x-1/2 text-8xl text-[#5fa292]/30 leading-none">
              "
            </span>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-[#333] italic font-normal leading-relaxed">
              {TESTIMONIALS[0].quote}
            </p>
            <p className="mt-8 text-sm uppercase tracking-[0.2em] text-[#3d3d3d]">
              - {TESTIMONIALS[0].name} · {TESTIMONIALS[0].detail}
            </p>
          </div>
          <Link to="/testimonials" className="inline-block mt-12" data-testid="home-testimonials-link">
            <Button
              variant="outline"
              className="border-[#5fa292] text-[#5fa292] hover:bg-[#cbdad6]/40 rounded-full px-8 bg-transparent"
            >
              Read more stories
            </Button>
          </Link>
        </div>
      </section>

      {/* PARENTS TEASER */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="rounded-3xl overflow-hidden bg-[#cbdad6]/40 border border-[#e5e0d8] grid md:grid-cols-2">
          <div className="aspect-[4/3] md:aspect-auto bg-[#cbdad6]">
            <img
              src="/brand/teens-acne.png"
              alt="Young woman in natural light"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-10 md:p-14 flex flex-col justify-center">
            <Overline>For parents & teens</Overline>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight font-normal text-[#333] mt-4">
              Worried about your daughter?
              <br />
              <span className="italic">There is another way.</span>
            </h2>
            <p className="mt-6 text-lg text-[#3d3d3d] font-normal leading-relaxed">
              Acne, painful periods, mood swings, irregular cycles. Today's
              standard answer is the pill. We can do so much better than
              that. A real, gentle, root-cause path for young women.
            </p>
            <Link to="/for-parents" className="inline-block mt-8" data-testid="home-parents-link">
              <Button className="bg-[#5fa292] hover:bg-[#4d8275] text-white rounded-full px-8 h-12 w-fit">
                Learn more
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* AIMA MEANING */}
      <AimaMeaning testId="home-aima-meaning" />

      {/* CTA BLOCK */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="rounded-3xl p-12 md:p-20 text-center bg-[#5fa292] text-white relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-white/10 blur-2xl pointer-events-none" aria-hidden />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-[#cbdad6]/30 blur-2xl pointer-events-none" aria-hidden />
            <p className="relative text-xs uppercase tracking-[0.25em] text-white/80">
              It's time to take action
            </p>
            <h2 className="relative font-serif text-4xl md:text-5xl lg:text-6xl mt-5 font-normal tracking-tight leading-tight">
              Ready to feel like
              <br />
              your best self?
            </h2>
            <p className="relative mt-6 text-lg text-white/90 max-w-2xl mx-auto font-normal">
              Book a complimentary 20-minute discovery call. No pressure, no
              prescriptions, just a real conversation about what's been
              happening.
            </p>
            <Link to="/contact" className="relative inline-block mt-10" data-testid="home-cta-final">
              <Button className="bg-white text-[#5fa292] hover:bg-[#faf9f6] rounded-full px-10 h-14 text-base font-semibold">
                Book your discovery call
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
